import { Box, Stack, Typography, useTheme } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { Check, ChevronRight } from "lucide-react";
import type { FC, ReactNode } from "react";
import { IconBox } from "web_myplann/Shared/Components/IconBox";

const TRANSITION = "background-color 200ms cubic-bezier(0.16, 1, 0.3, 1)";

export type ListRowProps = {
	title: ReactNode;
	subtitle?: ReactNode;
	tabularSubtitle?: boolean;
	labelFirst?: boolean;
	wrapTitle?: boolean;
	icon?: ReactNode;
	iconBg?: string;
	rightText?: ReactNode;
	rightTextStrong?: boolean;
	rightSlot?: ReactNode;
	showChevron?: boolean;
	isLast?: boolean;
	destructive?: boolean;
	selected?: boolean;
	pickerSelected?: boolean;
	dimmed?: boolean;
	disabled?: boolean;
	onClick?: () => void;
	sx?: SxProps<Theme>;
};

export const ListRow: FC<ListRowProps> = ({
	title,
	subtitle,
	tabularSubtitle = false,
	labelFirst = false,
	wrapTitle = false,
	icon,
	iconBg,
	rightText,
	rightTextStrong = false,
	rightSlot,
	showChevron = false,
	isLast = false,
	destructive = false,
	selected = false,
	pickerSelected = false,
	dimmed = false,
	disabled = false,
	onClick,
	sx,
}) => {
	const theme = useTheme();
	const { palette } = theme;

	const titleColor = (() => {
		if (destructive) return palette.red[500];
		if (pickerSelected) return palette.blue[700];
		if (dimmed) return palette.grey[500];
		return palette.grey[900];
	})();

	const subtitleColor = dimmed ? palette.grey[400] : palette.grey[500];
	const activeBg = pickerSelected
		? palette.blue[100]
		: selected
			? palette.blue[50]
			: palette.grey[50];
	const rowBg = pickerSelected ? palette.blue[50] : "transparent";

	const element = onClick ? "button" : "div";
	const isButton = element === "button";

	return (
		<Box
			component={element}
			{...(isButton ? { type: "button" as const, disabled } : {})}
			onClick={disabled ? undefined : onClick}
			sx={[
				{
					width: "100%",
					display: "flex",
					alignItems: wrapTitle ? "flex-start" : "center",
					gap: 1.2,
					px: 1.6,
					py: 1.2,
					textAlign: "left",
					bgcolor: rowBg,
					border: "none",
					borderBottom: isLast
						? "none"
						: theme.colors.border.solid(palette.grey[100]),
					cursor: onClick ? (disabled ? "not-allowed" : "pointer") : "default",
					transition: TRANSITION,
					opacity: disabled ? 0.5 : 1,
					pointerEvents: disabled ? "none" : "auto",
					...(disabled || !onClick
						? {}
						: { "&:active": { bgcolor: activeBg } }),
				},
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			{icon && iconBg && (
				<Box flexShrink={0} sx={{ opacity: dimmed ? 0.6 : 1 }}>
					<IconBox bg={iconBg}>{icon}</IconBox>
				</Box>
			)}
			{icon && !iconBg && (
				<Stack
					alignItems="center"
					justifyContent="center"
					flexShrink={0}
					sx={{ opacity: dimmed ? 0.6 : 1 }}
				>
					{icon}
				</Stack>
			)}
			<Stack
				flex={1}
				minWidth={0}
				direction={labelFirst ? "column-reverse" : "column"}
			>
				<Typography
					variant={pickerSelected ? "bodyStrong" : "bodyMedium"}
					color={titleColor}
					sx={
						wrapTitle
							? { overflowWrap: "anywhere" }
							: {
									overflow: "hidden",
									whiteSpace: "nowrap",
									textOverflow: "ellipsis",
								}
					}
				>
					{title}
				</Typography>
				{subtitle && (
					<Typography
						variant="caption"
						color={subtitleColor}
						tabular={tabularSubtitle}
						overflow="hidden"
						whiteSpace="nowrap"
						textOverflow="ellipsis"
					>
						{subtitle}
					</Typography>
				)}
			</Stack>
			{rightText && (
				<Typography
					variant={rightTextStrong ? "bodyStrong" : "bodyMedium"}
					color={rightTextStrong ? palette.grey[900] : palette.grey[500]}
					tabular
					flexShrink={0}
				>
					{rightText}
				</Typography>
			)}
			{rightSlot}
			{pickerSelected && !rightSlot && (
				<Check
					size={theme.iconSize.sm}
					color={palette.blue[600]}
					style={{ flexShrink: 0 }}
				/>
			)}
			{showChevron && !rightSlot && !pickerSelected && (
				<ChevronRight
					size={theme.iconSize.sm}
					color={palette.grey[400]}
					style={{ flexShrink: 0 }}
				/>
			)}
		</Box>
	);
};
